import {
  ArchiveRole,
  CareerPeriodType,
  CareerType,
  MemberType,
} from '../../shared/enum/EnumRepository';
import type { SettingData } from '../../shared/dto/EntityRepository';
import { normalizeCareerSectionOrder } from '../../shared/util/careerSectionOrder';

// 기본 스텝들
export const careerSections = [
  { type: CareerType.EDU, title: 'Education', storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.PERIOD },
  { type: CareerType.WRT, title: 'Writing', storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.CNS, title: 'Consulting', storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.S_EXH, title: 'Solo Exhibition', storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.POINT },
  { type: CareerType.G_EXH, title: 'Group Exhibition', storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.POINT },
  { type: CareerType.BIEN_TRIEN, title: 'Biennial/Triennial', storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.POINT },
  { type: CareerType.PRJ, title: 'Project', storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.FAR, title: 'Fair', storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.POINT },
  { type: CareerType.PRZ, title: 'Prize', storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.GRT, title: 'Grant', storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.PRS, title: 'Press', storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.RSD, title: 'Residency', storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.PERIOD },
  { type: CareerType.RPS, title: 'Representation', storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.PERIOD },
  { type: CareerType.EXP, title: 'Professional Experience', storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.PERIOD },
  { type: CareerType.TCH, title: 'Teach', storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.PERIOD },
  { type: CareerType.RSH, title: 'Research', storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.PBL, title: 'Publication', storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.COL, title: 'Collections', storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
];

const SHARED_CAREER_TYPES = [
  CareerType.EDU,
  CareerType.PRJ,
  CareerType.PRZ,
  CareerType.GRT,
  CareerType.PRS,
  CareerType.RSD,
  CareerType.PBL,
] as const;

const ARTIST_CAREER_TYPES = new Set<CareerType>([
  ...SHARED_CAREER_TYPES,
  CareerType.S_EXH,
  CareerType.G_EXH,
  CareerType.BIEN_TRIEN,
  CareerType.FAR,
  CareerType.RPS,
  CareerType.COL,
]);

const NON_ARTIST_CAREER_TYPES = new Set<CareerType>([
  ...SHARED_CAREER_TYPES,
  CareerType.EXP,
  CareerType.WRT,
  CareerType.CNS,
  CareerType.TCH,
  CareerType.RSH,
]);

const GROUP_CAREER_TYPES = new Set<CareerType>([
  CareerType.EXP,
  CareerType.G_EXH,
  CareerType.BIEN_TRIEN,
  CareerType.PRJ,
  CareerType.FAR,
  CareerType.PRZ,
  CareerType.GRT,
  CareerType.PRS,
  CareerType.RSD,
  CareerType.RSH,
  CareerType.PBL,
]);

export const getDefaultCareerSections = (
  setting: Pick<SettingData, 'memberType' | 'archiveRole'> | null | undefined,
  orderedSections = careerSections,
) => {
  let visibleTypes: Set<CareerType>;

  if (setting?.memberType === MemberType.GROUP) {
    visibleTypes = GROUP_CAREER_TYPES;
  } else if (
    setting?.memberType === MemberType.INDIVIDUAL &&
    setting.archiveRole === ArchiveRole.ARTIST
  ) {
    visibleTypes = ARTIST_CAREER_TYPES;
  } else if (
    setting?.memberType === MemberType.INDIVIDUAL &&
    setting.archiveRole != null
  ) {
    visibleTypes = NON_ARTIST_CAREER_TYPES;
  } else {
    return orderedSections;
  }

  return orderedSections.filter((section) => visibleTypes.has(section.type));
};

/** 서버 순서를 metadata에 적용하고, 누락된 타입은 기본 순서로 보충한다. */
export const resolveCareerSections = (
  careerSectionOrder: readonly CareerType[] | null | undefined,
) => {
  const sectionByType = new Map(
    careerSections.map((section) => [section.type, section] as const),
  );

  return normalizeCareerSectionOrder(careerSectionOrder)
    .map((type) => sectionByType.get(type))
    .filter((section): section is (typeof careerSections)[number] => section != null);
};

/**
 * CareerType을 title로 변환하는 함수
 * @param careerType - 변환할 CareerType
 * @returns 해당 CareerType의 title, 없으면 빈 문자열 반환
 */
export const getCareerTypeTitle = (careerType: CareerType): string => {
  const section = careerSections.find(section => section.type === careerType);
  return section?.title || '';
};
